import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
// perf-optimized
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { TableOrderProvider } from './lib/table-context';
import { KitchenPauseProvider } from './lib/kitchen-pause-context';
import { LanguageProvider } from './lib/i18n';
import { KitchenPausedBanner } from './components/restaurant/KitchenPausedBanner';
import { getSubdomainSlug } from './lib/subdomain';
import { ScrollToTop } from './components/ui/ScrollToTop';
// Eager Customer Pages (Critical Path)
import { HomePage } from './pages/HomePage';
import { RestaurantPage } from './pages/RestaurantPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { RequireAdminAuth } from './components/admin/RequireAdminAuth';
import { RequirePlatformAdmin } from './components/admin/RequirePlatformAdmin';
import { PublicRestaurantGate } from './components/PublicRestaurantGate';
// Lazy Customer Pages
const RestaurantListingPage = lazy(() => import('./pages/RestaurantListingPage').then(m => ({
  default: m.RestaurantListingPage
})));
const MenuPage = lazy(() => import('./pages/MenuPage').then(m => ({
  default: m.MenuPage
})));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage').then(m => ({
  default: m.OrderConfirmationPage
})));
const TrackingPage = lazy(() => import('./pages/TrackingPage').then(m => ({
  default: m.TrackingPage
})));
const TablePage = lazy(() => import('./pages/TablePage').then(m => ({
  default: m.TablePage
})));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({
  default: m.OnboardingPage
})));
const TermsRestaurantsPage = lazy(() => import('./pages/TermsRestaurantsPage').then(m => ({
  default: m.TermsRestaurantsPage
})));
const NatuCafePage = lazy(() => import('./pages/NatuCafePage').then(m => ({
  default: m.NatuCafePage
})));
const InvoicePage = lazy(() => import('./pages/InvoicePage').then(m => ({
  default: m.InvoicePage
})));
const TicketPage = lazy(() => import('./pages/TicketPage').then(m => ({
  default: m.TicketPage
})));
// Lazy Admin Pages
const LoginPage = lazy(() => import('./pages/admin/LoginPage').then(m => ({
  default: m.LoginPage
})));
const NoizaMetricsPage = lazy(() => import('./pages/admin/NoizaMetricsPage').then(m => ({
  default: m.NoizaMetricsPage
})));
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage').then(m => ({
  default: m.DashboardPage
})));
const KDSPage = lazy(() => import('./pages/admin/KDSPage').then(m => ({
  default: m.KDSPage
})));
const InventoryPage = lazy(() => import('./pages/admin/InventoryPage').then(m => ({
  default: m.InventoryPage
})));
const MetricsPage = lazy(() => import('./pages/admin/MetricsPage').then(m => ({
  default: m.MetricsPage
})));
const MenuManagementPage = lazy(() => import('./pages/admin/MenuManagementPage').then(m => ({
  default: m.MenuManagementPage
})));
const ImportPage = lazy(() => import('./pages/admin/ImportPage').then(m => ({
  default: m.ImportPage
})));
const WaiterPOSPage = lazy(() => import('./pages/admin/WaiterPOSPage').then(m => ({
  default: m.WaiterPOSPage
})));
const StationSelectPage = lazy(() => import('./pages/admin/StationSelectPage').then(m => ({
  default: m.StationSelectPage
})));
const CajaOverviewPage = lazy(() => import('./pages/admin/CajaOverviewPage').then(m => ({
  default: m.CajaOverviewPage
})));
const POSCajaPage = lazy(() => import('./pages/admin/POSCajaPage').then(m => ({
  default: m.POSCajaPage
})));
const CancelledOrdersPage = lazy(() => import('./pages/admin/CancelledOrdersPage').then(m => ({
  default: m.CancelledOrdersPage
})));
const TableManagementPage = lazy(() => import('./pages/admin/TableManagementPage').then(m => ({
  default: m.TableManagementPage
})));
const TableQRPage = lazy(() => import('./pages/admin/TableQRPage').then(m => ({
  default: m.TableQRPage
})));
const MenuQRPage = lazy(() => import('./pages/admin/MenuQRPage').then(m => ({
  default: m.MenuQRPage
})));
const LoyaltyDashboardPage = lazy(() => import('./pages/admin/LoyaltyDashboardPage').then(m => ({
  default: m.LoyaltyDashboardPage
})));
const LoyaltyRedemptionPage = lazy(() => import('./pages/admin/LoyaltyRedemptionPage').then(m => ({
  default: m.LoyaltyRedemptionPage
})));
const WebOrdersPage = lazy(() => import('./pages/admin/WebOrdersPage').then(m => ({
  default: m.WebOrdersPage
})));
const ServicioExpressPage = lazy(() => import('./pages/admin/ServicioExpressPage').then(m => ({
  default: m.ServicioExpressPage
})));
const InvoiceRequestsPage = lazy(() => import('./pages/admin/InvoiceRequestsPage').then(m => ({
  default: m.InvoiceRequestsPage
})));
const CreateRestaurantPage = lazy(() => import('./pages/admin/CreateRestaurantPage').then(m => ({
  default: m.CreateRestaurantPage
})));
const DriversListPage = lazy(() => import('./pages/admin/DriversListPage').then(m => ({
  default: m.DriversListPage
})));
const CreateDriverPage = lazy(() => import('./pages/admin/CreateDriverPage').then(m => ({
  default: m.CreateDriverPage
})));
const VehiclesListPage = lazy(() => import('./pages/admin/VehiclesListPage').then(m => ({
  default: m.VehiclesListPage
})));
const CreateVehiclePage = lazy(() => import('./pages/admin/CreateVehiclePage').then(m => ({
  default: m.CreateVehiclePage
})));
const AssignmentsPage = lazy(() => import('./pages/admin/AssignmentsPage').then(m => ({
  default: m.AssignmentsPage
})));
const UsersAdminPage = lazy(() => import('./pages/admin/UsersAdminPage').then(m => ({
  default: m.UsersAdminPage
})));
const EnrollmentPage = lazy(() => import('./pages/admin/EnrollmentPage').then(m => ({
  default: m.EnrollmentPage
})));
const EnrollmentRestaurantPage = lazy(() => import('./pages/admin/EnrollmentRestaurantPage').then(m => ({
  default: m.EnrollmentRestaurantPage
})));
const EnrollmentMenuUpdatePage = lazy(() => import('./pages/admin/EnrollmentMenuUpdatePage').then(m => ({
  default: m.EnrollmentMenuUpdatePage
})));
const EnrollmentPlatformAdminPage = lazy(() => import('./pages/admin/EnrollmentPlatformAdminPage').then(m => ({
  default: m.EnrollmentPlatformAdminPage
})));
const EnrollmentRestaurantAdminPage = lazy(() => import('./pages/admin/EnrollmentRestaurantAdminPage').then(m => ({
  default: m.EnrollmentRestaurantAdminPage
})));
const EnrollmentMenuImagesPage = lazy(() => import('./pages/admin/EnrollmentMenuImagesPage').then(m => ({
  default: m.EnrollmentMenuImagesPage
})));
const ReceiptGeneratorPage = lazy(() => import('./pages/admin/ReceiptGeneratorPage').then(m => ({
  default: m.ReceiptGeneratorPage
})));
const GlobalAdminLoginPage = lazy(() => import('./pages/admin/GlobalAdminLoginPage').then(m => ({
  default: m.GlobalAdminLoginPage
})));
// Lazy Driver Pages
const DriverLoginPage = lazy(() => import('./pages/driver/DriverLoginPage').then(m => ({
  default: m.DriverLoginPage
})));
const DriverAppPage = lazy(() => import('./pages/driver/DriverAppPage').then(m => ({
  default: m.DriverAppPage
})));
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
// ─── MAINTENANCE GATE ───
// Slugs under maintenance — only accessible with ?dev=noiza2026
const MAINTENANCE_SLUGS = ['noiza.restaurant'];
function MaintenancePage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const handleUnlock = () => {
    if (code === 'noiza2026') {
      sessionStorage.setItem('noiza_dev_access', 'true');
      window.location.reload();
    } else {
      setError(true);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-0" data-mp="2vu7ng0mnmrps">
      <div className="max-w-md w-full text-center" data-id="element-1" data-mp="2qvcy0fu6m4wp">
        <div className="w-16 h-16 mx-auto mb-6 bg-[#141414] rounded-2xl flex items-center justify-center border border-white/10" data-id="element-2" data-mp="2lymj3vu45fcv">
          <span className="text-2xl" data-id="element-3" data-mp="2hxayk4q3jkbj">🔧</span>
        </div>
        <h1 className="text-2xl font-black text-white mb-2" data-id="element-4" data-mp="2d62nz5d15sws">
          En Mantenimiento
        </h1>
        <p className="text-white/40 text-sm mb-8" data-id="element-5" data-mp="2866mbcrt2dke">
          Este restaurante está temporalmente en modo de pruebas. Volveremos
          pronto.
        </p>
        <div className="space-y-3" data-id="element-6" data-mp="235il5y9v4gcg">
          <input type="password" placeholder="Código de acceso" value={code} onChange={e => {
          setCode(e.target.value);
          setError(false);
        }} onKeyDown={e => e.key === 'Enter' && handleUnlock()} className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent text-center" data-id="element-7" data-mp="1y8aaeir1nel8" />
          {error && <p className="text-[#CE1126] text-xs" data-id="element-8" data-mp="nnmdxyt3ake2">Código incorrecto</p>}
          <button onClick={handleUnlock} className="w-full py-3 rounded-xl bg-[#006847] text-white font-bold text-sm hover:bg-[#005a3d] transition-colors" data-id="element-9" data-mp="3m6kx3y2vw1if">
            Acceder
          </button>
        </div>
      </div>
    </div>;
}
/** Routes Capricho de Canela to PublicMenuPage (premium theme), others to RestaurantPage */
function SmartRestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Maintenance gate: check URL param or session
  if (slug && MAINTENANCE_SLUGS.includes(slug)) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasDevParam = urlParams.get('dev') === 'noiza2026';
    const hasSessionAccess = sessionStorage.getItem('noiza_dev_access') === 'true';
    if (hasDevParam && !hasSessionAccess) {
      sessionStorage.setItem('noiza_dev_access', 'true');
    }
    if (!hasDevParam && !hasSessionAccess) {
      return <MaintenancePage data-id="element-10" data-mp="2u3ct1yav12sd" />;
    }
  }
  if (slug === 'capricho-de-canela') {
    return <PublicMenuPage data-id="element-11" data-mp="1wh5m4hr32ycu" />;
  }
  return <RestaurantPage data-id="element-12" data-mp="1whyjis45kbib" />;
}
/** Redirects /admin/:slug → /admin/:slug/operacion (station select) */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/operacion`} replace data-id="element-13" data-mp="26ds7d7nbnrfe" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/operacion" replace data-id="element-14" data-mp="1dpmjic0z1mbc" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-15" data-mp="3hyxy0zu2zts9">{children}</TenantProvider>;
}
/**
 * Wrapper for marketplace admin routes that need TenantProvider.
 * TenantProvider reads :slug from useParams and handles auth + access.
 */
function TenantAdminRoute({
  children
}: {
  children: React.ReactNode;
}) {
  return <TenantProvider data-id="element-16" data-mp="349dz653mfgo8">{children}</TenantProvider>;
}
/**
 * Wrapper for a restaurant's PUBLIC customer routes.
 *
 * Blocks the whole public tree of that restaurant when
 * `restaurants.access_status` is not 'active'. In marketplace mode the slug
 * comes from `/r/:slug`; in subdomain mode it is passed explicitly. The
 * global marketplace routes are never wrapped, so they stay online.
 */
function PublicRestaurantRoute({
  slug,
  children
}: {
  slug?: string;
  children: React.ReactNode;
}) {
  return <PublicRestaurantGate slug={slug} data-id="element-17" data-mp="2knkgcjsqdpds">{children}</PublicRestaurantGate>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <SubdomainTenantProvider slug={subdomainSlug} data-id="element-18" data-mp="33d0vod92rme9">
        <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-20" data-mp="2plm25chjk2sy">
              <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-21" data-mp="2pjt42lkzuqwk"></div>
            </div>} data-id="element-19" data-mp="33bpkvth7pedn">
          <Routes data-id="element-22" data-mp="3hbqrlpk5rg11">
            {/* Customer Routes */}
            <Route path="/" element={<PublicRestaurantRoute slug={subdomainSlug} data-id="element-24" data-mp="3m4texfycdt8s">
                  <SmartRestaurantPage data-id="element-25" data-mp="1anrn7ith996f" />
                </PublicRestaurantRoute>} data-id="element-23" data-mp="3m3ibjh8t6ni7" />
            <Route path="/menu" element={<PublicRestaurantRoute slug={subdomainSlug} data-id="element-27" data-mp="1kevxw6c4e7vg">
                  <MenuPage data-id="element-28" data-mp="1kgfdrvtv6gwp" />
                </PublicRestaurantRoute>} data-id="element-26" data-mp="15of0qz6iq44d" />
            <Route path="/cart" element={<PublicRestaurantRoute slug={subdomainSlug} data-id="element-30" data-mp="rqgn5w00ubpx">
                  <CartPage data-id="element-31" data-mp="mqub3pvxj1ip" />
                </PublicRestaurantRoute>} data-id="element-29" data-mp="1fhcllbyzg565" />
            <Route path="/checkout" element={<PublicRestaurantRoute slug={subdomainSlug} data-id="element-33" data-mp="11lib4oik97qx">
                  <CheckoutPage data-id="element-34" data-mp="wnyz7v9c5725" />
                </PublicRestaurantRoute>} data-id="element-32" data-mp="11nlc23l94laf" />
            <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-36" data-mp="284glj2j7xtb2" />} data-id="element-35" data-mp="2d1p0ku92lh83" />
            <Route path="/tracking/:orderId" element={<TrackingPage data-id="element-38" data-mp="hncvws7v7oom" />} data-id="element-37" data-mp="2861e4mlz326a" />
            <Route path="/t/:tableId" element={<PublicRestaurantRoute slug={subdomainSlug} data-id="element-40" data-mp="ml4qxv3t3qx1">
                  <TablePage data-id="element-41" data-mp="qnht69jch6p2" />
                </PublicRestaurantRoute>} data-id="element-39" data-mp="ho4ri31gpmew" />
            {/* QR code routes — match QR URL format from TableQRPage */}
            <Route path="/mesa/:tableNumber/:token" element={<PublicRestaurantRoute slug={subdomainSlug} data-id="element-43" data-mp="10l5ytuihnym0">
                  <TablePage data-id="element-44" data-mp="15k8y0ybsfam2" />
                </PublicRestaurantRoute>} data-id="element-42" data-mp="vnw2duwommoo" />
            <Route path="/mesa/:tableNumber" element={<PublicRestaurantRoute slug={subdomainSlug} data-id="element-46" data-mp="1ecuajwotsl2g">
                  <TablePage data-id="element-47" data-mp="1ecar2bjj1omb" />
                </PublicRestaurantRoute>} data-id="element-45" data-mp="1aaz2m9ce39fp" />

            {/* Admin Routes */}
            <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-49" data-mp="1obruembb3qf4" />} data-id="element-48" data-mp="1ob9unfpfqu2g" />
            <Route path="/admin/login" element={<LoginPage data-id="element-51" data-mp="1ygi0n4dqtgp5" />} data-id="element-50" data-mp="1yg8angsp79jg" />
            <Route path="/admin/operacion" element={<RequireAdminAuth data-id="element-53" data-mp="1tigjiozvnrgb">
                  <StationSelectPage data-id="element-54" data-mp="1og92vm43rn7n" />
                </RequireAdminAuth>} data-id="element-52" data-mp="1tg3wvyj6cswx" />
            <Route path="/admin/dashboard" element={<RequireAdminAuth data-id="element-56" data-mp="2h5ey1h4fspbl">
                  <DashboardPage data-id="element-57" data-mp="2h4dboghz0gek" />
                </RequireAdminAuth>} data-id="element-55" data-mp="1kg7c47zwoe75" />
            <Route path="/admin/kds" element={<RequireAdminAuth data-id="element-59" data-mp="27f3i0w7hapoc">
                  <KDSPage data-id="element-60" data-mp="27eby4in8ugdw" />
                </RequireAdminAuth>} data-id="element-58" data-mp="2c7wmdiz1bvzw" />
            <Route path="/admin/caja" element={<RequireAdminAuth data-id="element-62" data-mp="2zwfy68hamqwq">
                  <CajaOverviewPage data-id="element-63" data-mp="2zxh8ubuedbdx" />
                </RequireAdminAuth>} data-id="element-61" data-mp="23di6pfalejg9" />
            <Route path="/admin/pos-caja" element={<RequireAdminAuth data-id="element-65" data-mp="t98jwx1fxpl0">
                  <POSCajaPage data-id="element-66" data-mp="ta9vd2mdce39" />
                </RequireAdminAuth>} data-id="element-64" data-mp="2v46akz3ni2xm" />
            <Route path="/admin/cancelled" element={<RequireAdminAuth data-id="element-68" data-mp="jhude5b2npid">
                  <CancelledOrdersPage data-id="element-69" data-mp="ogxh9mfbfpor" />
                </RequireAdminAuth>} data-id="element-67" data-mp="y8uusztsqtqn" />
            <Route path="/admin/inventory" element={<RequireAdminAuth data-id="element-71" data-mp="fgiu0hisk0im">
                  <InventoryPage data-id="element-72" data-mp="fgsiu1s3o1mq" />
                </RequireAdminAuth>} data-id="element-70" data-mp="ahpekjjy1zft" />
            <Route path="/admin/metrics" element={<RequireAdminAuth data-id="element-74" data-mp="5i358l78l3ca">
                  <MetricsPage data-id="element-75" data-mp="5j4fwokcbnth" />
                </RequireAdminAuth>} data-id="element-73" data-mp="k1kzwy2qxz8" />
            <Route path="/admin/menu" element={<RequireAdminAuth data-id="element-77" data-mp="1zwm50g0e8sqm">
                  <MenuManagementPage data-id="element-78" data-mp="1zvua77emez1e" />
                </RequireAdminAuth>} data-id="element-76" data-mp="1uykmpxaskxg7" />
            <Route path="/admin/import" element={<RequireAdminAuth data-id="element-80" data-mp="3fgmr1sglt842">
                  <ImportPage data-id="element-81" data-mp="3fd8r0qvexazs" />
                </RequireAdminAuth>} data-id="element-79" data-mp="3key40c273yqi" />
            <Route path="/admin/waiter" element={<RequireAdminAuth data-id="element-83" data-mp="3pbwvemuh5rl0">
                  <WaiterPOSPage data-id="element-84" data-mp="6zi6rdstuovw" />
                </RequireAdminAuth>} data-id="element-82" data-mp="3u6asqusp68sb" />
            <Route path="/admin/tables" element={<RequireAdminAuth data-id="element-86" data-mp="2zzmmk8tyhu4">
                  <TableManagementPage data-id="element-87" data-mp="gxfttxyyafmx" />
                </RequireAdminAuth>} data-id="element-85" data-mp="71cqiosd4aye" />
            <Route path="/admin/qr" element={<RequireAdminAuth data-id="element-89" data-mp="bws21a2wdurn">
                  <TableQRPage data-id="element-90" data-mp="2io9gf57cvx4f" />
                </RequireAdminAuth>} data-id="element-88" data-mp="by38jhnq9gmg" />
            <Route path="/admin/menu-qr" element={<RequireAdminAuth data-id="element-92" data-mp="1rx27hu8m3dm4">
                  <MenuQRPage data-id="element-93" data-mp="1rw0wtqvict4x" />
                </RequireAdminAuth>} data-id="element-91" data-mp="2dqrfa0zkym6z" />
            <Route path="/admin/loyalty" element={<RequireAdminAuth data-id="element-95" data-mp="21m2bxt5vv17p">
                  <LoyaltyDashboardPage data-id="element-96" data-mp="21n3vyn4vmy1k" />
                </RequireAdminAuth>} data-id="element-94" data-mp="1wuu0ysown7sf" />
            <Route path="/admin/loyalty/redemption" element={<RequireAdminAuth data-id="element-98" data-mp="18zhhuky0wb0g">
                  <LoyaltyRedemptionPage data-id="element-99" data-mp="190aprp8px6k6" />
                </RequireAdminAuth>} data-id="element-97" data-mp="26l5cp1etyl9v" />
            <Route path="/admin/invoice-requests" element={<RequireAdminAuth data-id="element-101" data-mp="1iygt8ba6akr5">
                  <InvoiceRequestsPage data-id="element-102" data-mp="1nvh02oeujrjb" />
                </RequireAdminAuth>} data-id="element-100" data-mp="1iw43vd235842" />

            {/* Platform Admin Routes (Subdomain) */}
            <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-104" data-mp="2zcgqfehmvwap">
                  <ReceiptGeneratorPage data-id="element-105" data-mp="nnikwiucazc6" />
                </RequirePlatformAdmin>} data-id="element-103" data-mp="2udlxvktv6tde" />

            {/* Catch-all */}
            <Route path="/factura/:paymentId" element={<InvoicePage data-id="element-107" data-mp="dx7empn6oe24" />} data-id="element-106" data-mp="iofr5ujt8fji" />
            <Route path="/ticket/:orderNumber" element={<TicketPage data-id="element-109" data-mp="dv4g1h7ytcls" />} data-id="element-108" data-mp="dw5tfscgeb6o" />
            <Route path="*" element={<Navigate to="/" replace data-id="element-111" data-mp="8x30355u5tej" />} data-id="element-110" data-mp="8ye0qw3zil1f" />
          </Routes>
        </Suspense>
      </SubdomainTenantProvider>;
  }
  // ─── MARKETPLACE MODE ───
  // When accessed via noizacr.com
  return <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-113" data-mp="3w1itzg68hmk6">
          <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-114" data-mp="3r4ir1e4okzxa"></div>
        </div>} data-id="element-112" data-mp="3w2kgcgsp9vh7">
      <Routes data-id="element-115" data-mp="3m6z7pzt4lwyh">
        {/* Marketplace Routes */}
        <Route path="/" element={<HomePage data-id="element-117" data-mp="1pbl0xq2aglg1" />} data-id="element-116" data-mp="1pb1j09cn1x20" />
        <Route path="/restaurants" element={<RestaurantListingPage data-id="element-119" data-mp="1kd01hsuv25sn" />} data-id="element-118" data-mp="1kd9s9inqcgze" />
        <Route path="/terminos-restaurantes" element={<TermsRestaurantsPage data-id="element-121" data-mp="2c6avrf2qc3eh" />} data-id="element-120" data-mp="1ke2ovqsz5q3t" />
        <Route path="/natucafe" element={<NatuCafePage data-id="element-123" data-mp="278j1wfiiy77n" />} data-id="element-122" data-mp="2c9f5st2vltd2" />
        <Route path="/onboarding" element={<OnboardingPage data-id="element-125" data-mp="27accu7067dlg" />} data-id="element-124" data-mp="279aqh6dpf4of" />

        {/* Shared customer routes (work without slug for legacy/direct links) */}
        <Route path="/cart" element={<CartPage data-id="element-127" data-mp="1xizm5iq3yth0" />} data-id="element-126" data-mp="22f6icjspcqlr" />
        <Route path="/checkout" element={<CheckoutPage data-id="element-129" data-mp="1xgdd1enkk5wo" />} data-id="element-128" data-mp="1xhohlgou9hou" />
        <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-131" data-mp="1thmdl0vmsc6e" />} data-id="element-130" data-mp="1tgl3ozqcpvq9" />

        {/* Restaurant Specific Routes */}
        <Route path="/r/:slug" element={<PublicRestaurantRoute data-id="element-133" data-mp="1jiotqb95h6tq">
              <SmartRestaurantPage data-id="element-134" data-mp="1jjq4sfq61tbg" />
            </PublicRestaurantRoute>} data-id="element-132" data-mp="1ohi59y4vmnr9" />
        <Route path="/r/:slug/menu" element={<PublicRestaurantRoute data-id="element-136" data-mp="3dwppuodbffq4">
              <MenuPage data-id="element-137" data-mp="3dvn0wlzjzncu" />
            </PublicRestaurantRoute>} data-id="element-135" data-mp="1ekdjw0iuuwbi" />
        <Route path="/r/:slug/cart" element={<PublicRestaurantRoute data-id="element-139" data-mp="38vsb6lril3td">
              <CartPage data-id="element-140" data-mp="3hff4fkoq0mw7" />
            </PublicRestaurantRoute>} data-id="element-138" data-mp="38x3ekkh1s9jy" />
        <Route path="/r/:slug/checkout" element={<PublicRestaurantRoute data-id="element-142" data-mp="3le4ccxqzpc8g">
              <CheckoutPage data-id="element-143" data-mp="3q6ff08d47c4p" />
            </PublicRestaurantRoute>} data-id="element-141" data-mp="3lfz16n5dtoht" />
        <Route path="/r/:slug/order-confirmation" element={<OrderConfirmationPage data-id="element-145" data-mp="3v58webvivna5" />} data-id="element-144" data-mp="3v60mj76cost1" />
        <Route path="/r/:slug/tracking/:orderId" element={<TrackingPage data-id="element-147" data-mp="2yhkkwcpyuszk" />} data-id="element-146" data-mp="2yg9iag89bra1" />
        <Route path="/r/:slug/t/:tableId" element={<PublicRestaurantRoute data-id="element-149" data-mp="32jfmlibt78dv">
              <TablePage data-id="element-150" data-mp="37gpnpv8k98hj" />
            </PublicRestaurantRoute>} data-id="element-148" data-mp="32hd0h9ds1zef" />
        {/* QR code routes — match QR URL format from TableQRPage */}
        <Route path="/r/:slug/mesa/:tableNumber/:token" element={<PublicRestaurantRoute data-id="element-152" data-mp="mpp3jdyrk6vm">
              <TablePage data-id="element-153" data-mp="msl4zc9tldot" />
            </PublicRestaurantRoute>} data-id="element-151" data-mp="3cgu31hxqfx66" />
        <Route path="/r/:slug/mesa/:tableNumber" element={<PublicRestaurantRoute data-id="element-155" data-mp="1zl54vuhqfqo">
              <TablePage data-id="element-156" data-mp="3t7h8j3s243qf" />
            </PublicRestaurantRoute>} data-id="element-154" data-mp="rregx09gkwmv" />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<GlobalAdminLoginPage data-id="element-158" data-mp="byammnih5y6e" />} data-id="element-157" data-mp="bz2blfhkgxnp" />
        <Route path="/admin/metrics" element={<RequirePlatformAdmin data-id="element-160" data-mp="71ka2a5vpkpq">
              <NoizaMetricsPage data-id="element-161" data-mp="6yemx0gt8swn" />
            </RequirePlatformAdmin>} data-id="element-159" data-mp="bx9byk5dfdp7" />
        <Route path="/admin/restaurants/new" element={<RequirePlatformAdmin data-id="element-163" data-mp="3aiizkzs3a1bl">
              <CreateRestaurantPage data-id="element-164" data-mp="3af4yrvz2q069" />
            </RequirePlatformAdmin>} data-id="element-162" data-mp="3fdybhm6jdn5d" />
        <Route path="/admin/drivers" element={<RequirePlatformAdmin data-id="element-166" data-mp="3kdkobuigd1dn">
              <DriversListPage data-id="element-167" data-mp="3kdazia95909j" />
            </RequirePlatformAdmin>} data-id="element-165" data-mp="3p7ow2fzjldfs" />
        <Route path="/admin/drivers/new" element={<RequirePlatformAdmin data-id="element-169" data-mp="2rpylioh1u0ru">
              <CreateDriverPage data-id="element-170" data-mp="2ronfei04sgxk" />
            </RequirePlatformAdmin>} data-id="element-168" data-mp="2wngirhlperk0" />
        <Route path="/admin/vehicles" element={<RequirePlatformAdmin data-id="element-172" data-mp="1bzxoe0tjsqn5">
              <VehiclesListPage data-id="element-173" data-mp="1c0z26d1y7r8k" />
            </RequirePlatformAdmin>} data-id="element-171" data-mp="176cp85g7xqmd" />
        <Route path="/admin/vehicles/new" element={<RequirePlatformAdmin data-id="element-175" data-mp="12a4agt5xnzfq">
              <CreateVehiclePage data-id="element-176" data-mp="12700ff5se9h5" />
            </RequirePlatformAdmin>} data-id="element-174" data-mp="x86nbnuewkhi" />
        <Route path="/admin/assignments" element={<RequirePlatformAdmin data-id="element-178" data-mp="1uwp1fsihy9ir">
              <AssignmentsPage data-id="element-179" data-mp="1uvnrjrd7vt2m" />
            </RequirePlatformAdmin>} data-id="element-177" data-mp="1pwubpsagjpza" />
        <Route path="/admin/users" element={<RequirePlatformAdmin data-id="element-181" data-mp="1kyj2njrzlji4">
              <UsersAdminPage data-id="element-182" data-mp="1l1x3gnl05kng" />
            </RequirePlatformAdmin>} data-id="element-180" data-mp="1h0bngtd68q91" />
        <Route path="/admin/enrollment" element={<RequirePlatformAdmin data-id="element-184" data-mp="9lqh4qpshrai">
              <EnrollmentPage data-id="element-185" data-mp="9ikpb3ps0bb3" />
            </RequirePlatformAdmin>} data-id="element-183" data-mp="5hsfo9czs4ea" />
        <Route path="/admin/enrollment/restaurant" element={<RequirePlatformAdmin data-id="element-187" data-mp="l0blcwq2m8gi">
              <EnrollmentRestaurantPage data-id="element-188" data-mp="kza60g20uzsz" />
            </RequirePlatformAdmin>} data-id="element-186" data-mp="pt4ndcu5n2ow" />
        <Route path="/admin/enrollment/menu-update" element={<RequirePlatformAdmin data-id="element-190" data-mp="b4qn99o4hsc1">
              <EnrollmentMenuUpdatePage data-id="element-191" data-mp="18qfnt0riw25x" />
            </RequirePlatformAdmin>} data-id="element-189" data-mp="fznucb1udrma" />
        <Route path="/admin/enrollment/platform-admin" element={<RequirePlatformAdmin data-id="element-193" data-mp="13prug8ftn98j">
              <EnrollmentPlatformAdminPage data-id="element-194" data-mp="yrqe3v9s5o0r" />
            </RequirePlatformAdmin>} data-id="element-192" data-mp="13rl3fudwq5jp" />
        <Route path="/admin/enrollment/restaurant-admin" element={<RequirePlatformAdmin data-id="element-196" data-mp="3jj8q8wz7ihos">
              <EnrollmentRestaurantAdminPage data-id="element-197" data-mp="3jkae621bmynx" />
            </RequirePlatformAdmin>} data-id="element-195" data-mp="uroa3fgkoiib" />
        <Route path="/admin/enrollment/menu-images" element={<RequirePlatformAdmin data-id="element-199" data-mp="1v04majvkfsyf">
              <EnrollmentMenuImagesPage data-id="element-200" data-mp="1ux094x04hmvm" />
            </RequirePlatformAdmin>} data-id="element-198" data-mp="3ffkhwc2r3i2h" />
        <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-202" data-mp="23y86ncutejon">
              <ReceiptGeneratorPage data-id="element-203" data-mp="28qhpk8xiijhf" />
            </RequirePlatformAdmin>} data-id="element-201" data-mp="1yyls90392xe9" />
        <Route path="/admin/enrollment/metrics" element={<RequirePlatformAdmin data-id="element-205" data-mp="2dpb7qenquynx">
              <NoizaMetricsPage data-id="element-206" data-mp="2iofd0u7jsc8z" />
            </RequirePlatformAdmin>} data-id="element-204" data-mp="28scedybwmvqs" />

        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-208" data-mp="2mq0uytyxvgq1" />} data-id="element-207" data-mp="2mq9ayuqcz25g" />
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-210" data-mp="2rp3s7s8oging" />} data-id="element-209" data-mp="2mqslhqdoio9g" />
        <Route path="/admin/:slug/operacion" element={<TenantAdminRoute data-id="element-212" data-mp="schubb9m3i0i">
              <StationSelectPage data-id="element-213" data-mp="sdb0ob4ns5i4" />
            </TenantAdminRoute>} data-id="element-211" data-mp="2rply7gl56b8k" />
        <Route path="/admin/:slug/dashboard" element={<TenantAdminRoute data-id="element-215" data-mp="20lysbd1uezpo">
              <DashboardPage data-id="element-216" data-mp="20n9uj8fn3zeo" />
            </TenantAdminRoute>} data-id="element-214" data-mp="xajcltz7rdax" />
        <Route path="/admin/:slug/kds" element={<TenantAdminRoute data-id="element-218" data-mp="1vpi28db32b9y">
              <KDSPage data-id="element-219" data-mp="29n6q7u0g5yj8" />
            </TenantAdminRoute>} data-id="element-217" data-mp="1vnousvsnbn0w" />
        <Route path="/admin/:slug/caja" element={<TenantAdminRoute data-id="element-221" data-mp="24py79r7h5qgx">
              <CajaOverviewPage data-id="element-222" data-mp="2jkuo2stcnm5m" />
            </TenantAdminRoute>} data-id="element-220" data-mp="24ntu0inorf3z" />
        <Route path="/admin/:slug/pos-caja" element={<TenantAdminRoute data-id="element-224" data-mp="2elri965jpbwl">
              <POSCajaPage data-id="element-225" data-mp="2sdelxr1thmpe" />
            </TenantAdminRoute>} data-id="element-223" data-mp="2jirphke4skpa" />
        <Route path="/admin/:slug/cancelled" element={<TenantAdminRoute data-id="element-227" data-mp="2occnhcfq5eoh">
              <CancelledOrdersPage data-id="element-228" data-mp="32blrldqnoic3" />
            </TenantAdminRoute>} data-id="element-226" data-mp="2sfiu4l6r17vh" />
        <Route path="/admin/:slug/inventory" element={<TenantAdminRoute data-id="element-230" data-mp="2xe2fajdiqfms">
              <InventoryPage data-id="element-231" data-mp="2xaodpdcoiage" />
            </TenantAdminRoute>} data-id="element-229" data-mp="329hrz7zsi5hm" />
        <Route path="/admin/:slug/metrics" element={<TenantAdminRoute data-id="element-233" data-mp="3lw2v37ytu831">
              <MetricsPage data-id="element-234" data-mp="3lv1gitilr3gk" />
            </TenantAdminRoute>} data-id="element-232" data-mp="3gw81gwno34ea" />
        <Route path="/admin/:slug/menu" element={<TenantAdminRoute data-id="element-236" data-mp="3by6jkf1z9b4e">
              <MenuManagementPage data-id="element-237" data-mp="3bxdav8jgkbjm" />
            </TenantAdminRoute>} data-id="element-235" data-mp="36zllal6ad1il" />
        <Route path="/admin/:slug/import" element={<TenantAdminRoute data-id="element-239" data-mp="32yrwzqoxlkp6">
              <ImportPage data-id="element-240" data-mp="32zujznj4v2zt" />
            </TenantAdminRoute>} data-id="element-238" data-mp="2y68hgo6oofhq" />
        <Route path="/admin/:slug/waiter" element={<TenantAdminRoute data-id="element-242" data-mp="2t86zk6kzum7u">
              <WaiterPOSPage data-id="element-243" data-mp="2t9hynt3hv5sm" />
            </TenantAdminRoute>} data-id="element-241" data-mp="2oaoq8f32e4zb" />
        <Route path="/admin/:slug/tables" element={<TenantAdminRoute data-id="element-245" data-mp="2jaao1do59p97">
              <TableManagementPage data-id="element-246" data-mp="2j9hhodt3l1rl" />
            </TenantAdminRoute>} data-id="element-244" data-mp="2f8p79h8hoqtq" />
        <Route path="/admin/:slug/qr" element={<TenantAdminRoute data-id="element-248" data-mp="2uqysewjalfxn">
              <TableQRPage data-id="element-249" data-mp="2us0678rp0gj2" />
            </TenantAdminRoute>} data-id="element-247" data-mp="2zqbhzp1jt13x" />
        <Route path="/admin/:slug/menu-qr" element={<TenantAdminRoute data-id="element-251" data-mp="2ptox50982a1w">
              <MenuQRPage data-id="element-252" data-mp="2ktapvkfg344x" />
            </TenantAdminRoute>} data-id="element-250" data-mp="2prlzpv5qpen5" />
        <Route path="/admin/:slug/loyalty" element={<TenantAdminRoute data-id="element-254" data-mp="2gtaanxkiuowt">
              <LoyaltyDashboardPage data-id="element-255" data-mp="2c0quqtyd3hou" />
            </TenantAdminRoute>} data-id="element-253" data-mp="2gr79chdx59cs" />
        <Route path="/admin/:slug/loyalty/redemption" element={<TenantAdminRoute data-id="element-257" data-mp="26z1i59vhqvyn">
              <LoyaltyRedemptionPage data-id="element-258" data-mp="222t6i6gi5kw8" />
            </TenantAdminRoute>} data-id="element-256" data-mp="271dyxjpij0a4" />
        <Route path="/admin/:slug/invoice-requests" element={<TenantAdminRoute data-id="element-260" data-mp="1t1lbbx9a906d">
              <InvoiceRequestsPage data-id="element-261" data-mp="1t1cyu6h2nwvp" />
            </TenantAdminRoute>} data-id="element-259" data-mp="1x2ygs68gr1cr" />
        {/* Emilius Pizza — Pedidos Web y Servicio Express. Ambas
            pantallas se auto-restringen al restaurante 27; para
            cualquier otro muestran "Módulo no disponible". */}
        <Route path="/admin/:slug/pedidos-web" element={<TenantAdminRoute data-id="element-263" data-mp="hifs9fezzbiy">
              <WebOrdersPage data-id="element-264" data-mp="mjlklc4qwwry" />
            </TenantAdminRoute>} data-id="element-262" data-mp="hkigbtwlaul1" />
        <Route path="/admin/:slug/servicio-express" element={<TenantAdminRoute data-id="element-266" data-mp="3kz4n4457wwss">
              <ServicioExpressPage data-id="element-267" data-mp="3pzaj1wiit5go" />
            </TenantAdminRoute>} data-id="element-265" data-mp="3l190dcp0b85q" />

        {/* Driver App Routes */}
        <Route path="/driver/login" element={<DriverLoginPage data-id="element-269" data-mp="3ux2dax6n13o1" />} data-id="element-268" data-mp="3uyddyo4sdvax" />
        <Route path="/driver/app" element={<DriverAppPage data-id="element-271" data-mp="3l99hku9xctq" />} data-id="element-270" data-mp="3kzibtxhszmg" />

        {/* Catch-all */}
        <Route path="/factura/:paymentId" element={<InvoicePage data-id="element-273" data-mp="329oltelpl0bb" />} data-id="element-272" data-mp="32ar9ldnqimn0" />
        <Route path="/ticket/:orderNumber" element={<TicketPage data-id="element-275" data-mp="37919u2obgdfh" />} data-id="element-274" data-mp="328dg398pdihk" />
        <Route path="*" element={<Navigate to="/" replace data-id="element-277" data-mp="377hpo0zjhi8f" />} data-id="element-276" data-mp="3789l9bt4zfyp" />
      </Routes>
    </Suspense>;
}
export function App() {
  // ─── Disable browser auto-translate for the whole app ───
  // Some mobile browsers (Chrome, Safari) auto-translate menu items like
  // "Frozen Capuccino" → "Capuchino helado". We set the document language
  // to Spanish and inject the Google notranslate meta so the menu names
  // are always shown exactly as stored. The translate="no" on the root
  // div below is inherited by every descendant (products, categories,
  // prices, modals, cart, checkout).
  useEffect(() => {
    try {
      document.documentElement.lang = 'es';
      const metaName = 'google';
      let meta = document.querySelector(`meta[name="${metaName}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = metaName;
        document.head.appendChild(meta);
      }
      meta.content = 'notranslate';
    } catch {
      /* silent */
    }
  }, []);
  return <LanguageProvider data-id="element-278" data-mp="1kb7edmmk7cdv">
      <CartProvider data-id="element-279" data-mp="10n2w5zdlr4s9">
        <TableOrderProvider data-id="element-280" data-mp="10onrhr7qqrr6">
          <KitchenPauseProvider data-id="element-281" data-mp="10nurd93af0i0">
            <BrowserRouter data-id="element-282" data-mp="vohya6ltox6z">
              <ScrollToTop data-id="element-283" data-mp="vpb1wyphj6kw" />
              <KitchenPausedBanner data-id="element-284" data-mp="vq2t7xc1ui5d" />
              <div translate="no" className="notranslate min-h-screen w-full flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-285" data-mp="19oaughg4c5mc">
                <AppRoutes data-id="element-286" data-mp="15p20pcl2w7qs" />
              </div>
            </BrowserRouter>
          </KitchenPauseProvider>
        </TableOrderProvider>
      </CartProvider>
    </LanguageProvider>;
}